import { Alert, Button, Typography } from 'antd'
import { useCurrentUser, useJurisdictions, useUpdateUser } from 'api'
import Container from 'components/Container'
import { useHideModal } from 'components/Modals'
import { Formik } from 'formik'
import { Form, FormItem, Select } from 'formik-antd'
import { useLocalization } from 'localization'
import { useMemo } from 'react'
import { createValidationSchema } from 'utils/validation'
import JurisdictionProperties from './JurisdictionProperties'
import Skeleton from './skeleton'
import { ITaxesFormValues } from './types'

const TaxesTab = () => {
	const { t } = useLocalization()
	const { data: user, isLoading: isUserLoading, isFetched: isUserFetched } = useCurrentUser()
	const {
		data: jurisdictions = [],
		jurisdictionOptions = [],
		isLoading: areJurisdictionsLoading,
		isFetched: areJurisdictionsFetched,
	} = useJurisdictions()
	const { mutateAsync: updateUser } = useUpdateUser()
	const hideModal = useHideModal()

	const schema = useMemo(
		() =>
			createValidationSchema(yup =>
				yup.object({
					jurisdiction: yup.string().required(),
					method: yup.string().required(),
				})
			),
		[]
	)

	const isLoading = isUserLoading || areJurisdictionsLoading
	const isFetched = isUserFetched || areJurisdictionsFetched

	const onSubmit = async (values: ITaxesFormValues) => {
		if (user) {
			await updateUser({
				id: user.id,
				jurisdiction: values.jurisdiction,
				properties:
					values?.properties && Object.keys(values.properties as object).length === 0 ? undefined : values.properties,
				costBasisMethod: values.method,
			})
			hideModal()
		}
	}

	const initialValue: ITaxesFormValues = {
		jurisdiction: user?.jurisdiction || '',
		method: user?.costBasisMethod || '',
		properties: user?.properties,
	}

	return (
		<Formik initialValues={initialValue} validationSchema={schema} enableReinitialize onSubmit={onSubmit}>
			{({ values, setFieldValue, dirty, isSubmitting }) => {
				const foundJurisdiction = jurisdictions.find(({ shortName }) => shortName === values.jurisdiction)

				const foundMethod = foundJurisdiction?.costBasisMethods.find(({ name }) => name === values.method)

				return (
					<Form layout="vertical">
						{(isLoading && !isFetched) || !foundJurisdiction ? (
							<Skeleton />
						) : (
							<>
								<Container margin={{ bottom: 'md' }}>
									<Alert message={t('settings.taxes.changeWarning')} type="info"></Alert>
								</Container>
								<FormItem
									name="jurisdiction"
									label={t('settings.taxes.jurisdiction')}
									required
									{...(foundJurisdiction?.currency
										? {
												extra: t('settings.taxes.currency', { currency: foundJurisdiction.currency }),
										  }
										: undefined)}
								>
									<Select
										name="jurisdiction"
										placeholder={t('placeholders.jurisdiction')}
										onChange={newValue => {
											const newJurisdiction = jurisdictions.find(({ shortName }) => shortName === newValue)
											setFieldValue('method', newJurisdiction?.costBasisMethods?.[0]?.name)
										}}
										options={jurisdictionOptions}
									/>
								</FormItem>
								<FormItem
									name="method"
									label={t('settings.taxes.costBasisMethod')}
									required
									{...(foundMethod && !foundMethod?.isAllowed
										? {
												extra: (
													<Typography.Text type="danger">{t('settings.taxes.notAllowedMethodWarning')}</Typography.Text>
												),
										  }
										: undefined)}
								>
									<Select
										name="method"
										placeholder={t('placeholders.costBasisMethod')}
										options={
											foundJurisdiction?.costBasisMethods.map(({ name }) => ({
												label: t(`settings.costBasisMethods.${name}`),
												value: name,
											})) || []
										}
									/>
								</FormItem>
								<JurisdictionProperties />
							</>
						)}
						<Button
							type="primary"
							htmlType="submit"
							loading={isSubmitting}
							disabled={areJurisdictionsLoading || !dirty}
						>
							{t('actions.save')}
						</Button>
					</Form>
				)
			}}
		</Formik>
	)
}

export default TaxesTab
